import { useMemo } from 'react'
import * as React from 'react'
import { useIntl } from 'react-intl'
import Group from 'components/utils/Group'
import BodyText from 'LuxKit/Typography/BodyText'
import OfferInclusionItem from 'components/Common/Inclusions/OfferInclusionItem'
import CruiseLuxPlusInclusionList from './CruiseLuxPlusInclusionList'

import { buildAllStandardInclusions } from 'lib/cruises/cruiseInclusionsUtils'
import styled from 'styled-components'
import { rem } from 'polished'
import LabeledSheet from 'LuxKit/LabeledSheet'
import PromotionLabel from 'LuxKit/Label/PromotionLabel'
import SolidFireIcon from 'LuxKit/Icons/solid/SolidFireIcon'
import VerticalSpacer from 'components/Common/Spacing/VerticalSpacer'

const InclusionList = styled(VerticalSpacer)`
  padding: ${rem(8)};
`

interface Props {
  offer: App.CruiseOffer
  evergreenInclusions?: Array<App.EvergreenInclusion>
  tileInclusions?: Array<App.OfferInclusion>
  standardInclusions: Array<App.PackageInclusion>
  luxPlusInclusions: Array<App.PackageInclusion>
  gap?: React.ComponentProps<typeof Group>['gap']
}

function CruiseInclusionList({ offer, standardInclusions, luxPlusInclusions, evergreenInclusions, tileInclusions, gap = 8 }: Props) {
  const intl = useIntl()
  const allStandardInclusions = useMemo(() => {
    return buildAllStandardInclusions(evergreenInclusions, standardInclusions, tileInclusions)
  }, [evergreenInclusions, standardInclusions, tileInclusions])

  const hasStandardInclusions = !!allStandardInclusions.length
  const hasInclusions = hasStandardInclusions || !!luxPlusInclusions?.length

  return <>
    {hasInclusions && <Group direction="vertical" gap={gap} horizontalAlign="start">
      <CruiseLuxPlusInclusionList luxPlusInclusions={luxPlusInclusions} offer={offer} />

      {hasStandardInclusions && <LabeledSheet
        kind="highlight"
        label={<PromotionLabel icon={<SolidFireIcon />} variant="premium">
          {intl.formatMessage({
            id: '0gNTLTDxVcCH7_pXXfH6',
            defaultMessage: 'Special offer',
            description: 'Label for special offer on cruise inclusions in the cruise section',
          })}
        </PromotionLabel>}
      >
        <InclusionList gap={4}>
          {allStandardInclusions.map((inclusion) => <OfferInclusionItem
            key={inclusion.id}
            inclusion={inclusion}
            colour={inclusion.isHighlighted ? 'highlight-secondary' : undefined}
            showMinStayDescription
          />)}
          <BodyText variant="small" colour="neutral-one">
            {intl.formatMessage({
              id: 'gWMOgND2ZhSab_HT-_aV',
              defaultMessage: 'Available on select packages',
              description: 'Note indicating inclusions are available on select cruise packages',
            })}
          </BodyText>
        </InclusionList>
      </LabeledSheet>}
    </Group>}
  </>
}

export default CruiseInclusionList
